import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Avatar,
  Divider,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Badge,
  FormControl,
  InputLabel,
  Select,
  InputAdornment
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
  CheckCircle as ApproveIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  LinkedIn as LinkedInIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import socketService from '../../services/SocketService';
import ApiService from '../../services/api';

const JobApplications = () => {
  const { currentUser } = useAuth();
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [statusUpdateDialogOpen, setStatusUpdateDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusError, setStatusError] = useState('');
  const [interviewDialogOpen, setInterviewDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [jobFilter, setJobFilter] = useState('all');
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  // Interview scheduling state
  const [interviewData, setInterviewData] = useState({
    date: '',
    time: '',
    type: 'video',
    location: '',
    meetingLink: '',
    notes: ''
  });

  const statusColors = {
    submitted: 'default',
    under_review: 'info',
    shortlisted: 'primary',
    interview_scheduled: 'warning',
    interviewed: 'secondary',
    offer_extended: 'success',
    accepted: 'success',
    rejected: 'error',
    withdrawn: 'default'
  };

  const statusLabels = {
    submitted: 'Submitted',
    under_review: 'Under Review',
    shortlisted: 'Shortlisted',
    interview_scheduled: 'Interview Scheduled',
    interviewed: 'Interviewed',
    offer_extended: 'Offer Extended',
    accepted: 'Accepted',
    rejected: 'Rejected',
    withdrawn: 'Withdrawn'
  };

  

  const fetchApplications = useCallback(async () => {
    try {
      // Real data: applications for this employer
      const resp = await ApiService.getApplicationsByEmployer(currentUser?._id || currentUser?.uid);
      const rawApps = resp.applications || resp || [];

      const mapped = rawApps.map(app => {
        const applicantName = [app.applicant?.firstName, app.applicant?.lastName]
          .filter(Boolean)
          .join(' ');
        return {
          id: app._id,
          jobId: app.job?._id,
          jobTitle: app.job?.title || 'Untitled Job',
          applicant: {
            id: app.applicant?._id,
            name: applicantName || app.applicant?.email || 'Applicant',
            email: app.applicant?.email || 'N/A',
            phone: app.applicant?.phone || '',
            avatar: null,
            linkedIn: app.applicant?.linkedIn || ''
          },
          status: app.status || 'submitted',
          appliedDate: app.createdAt || app.appliedDate || new Date().toISOString(),
          coverLetter: app.coverLetter || '',
          resume: app.resume || '',
          experience: app.experience || '',
          expectedSalary: app.expectedSalary || '',
          availableFrom: app.availableFrom || '',
          interview: app.interviewDetails ? {
            date: app.interviewDetails.date || '',
            time: app.interviewDetails.time || '',
            type: app.interviewDetails.type || 'video',
            meetingLink: app.interviewDetails.meetingLink || ''
          } : undefined
        };
      });

      setApplications(mapped);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setAlert({
        show: true,
        message: 'Failed to load applications',
        severity: 'error'
      });
    }
  }, [currentUser]);

  const fetchJobs = useCallback(async () => {
    try {
      const resp = await ApiService.getMyJobs({ limit: 100 });
      const rawJobs = resp.jobs || resp || [];
      const mappedJobs = rawJobs.map(j => ({
        id: j._id || j.id,
        title: j.title || 'Untitled',
        applicationsCount: j.applicationsCount || (Array.isArray(j.applications) ? j.applications.length : 0)
      }));
      setJobs(mappedJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
    fetchJobs();

    const unsubscribeJobApplication = socketService.subscribe('job_application', (data) => {
      setApplications(prev => {
        const newApplication = {
          id: data.applicationId,
          jobId: data.jobId,
          jobTitle: data.jobTitle,
          applicant: {
            id: data.applicantId,
            name: data.applicantName,
            email: data.applicantEmail,
            phone: data.applicantPhone || '',
            avatar: null
          },
          status: 'submitted',
          appliedDate: new Date().toISOString().split('T')[0],
          coverLetter: data.coverLetter || '',
          resume: data.resumeUrl || '',
          experience: data.experience || '',
          expectedSalary: data.expectedSalary || '',
          availableFrom: data.availableFrom || ''
        };
        return [newApplication, ...prev];
      });

      setAlert({
        show: true,
        message: `New application received from ${data.applicantName} for ${data.jobTitle}`,
        severity: 'info'
      });
    });

    return () => {
      unsubscribeJobApplication();
    };
  }, [fetchApplications, fetchJobs]);

  const handleStatusUpdate = async (applicationId, newStatus) => {
    try {
      const resp = await ApiService.updateApplicationStatus(applicationId, newStatus);
      const updated = resp.application || resp?.data?.application;

      setApplications(prev =>
        prev.map(app =>
          app.id === applicationId
            ? { ...app, status: updated?.status || newStatus }
            : app
        )
      );

      setSelectedApplication(prev =>
        prev && prev.id === applicationId
          ? { ...prev, status: updated?.status || newStatus }
          : prev
      );

      setAlert({
        show: true,
        message: `Application status updated to ${statusLabels[updated?.status || newStatus]}`,
        severity: 'success'
      });
      setStatusUpdateDialogOpen(false);
      setNewStatus('');
      setStatusError('');
    } catch (error) {
      console.error('Error updating status:', error);
      setAlert({
        show: true,
        message: 'Failed to update application status',
        severity: 'error'
      });
    }
  };

  const handleScheduleInterview = async () => {
    try {
      // Mock API call - replace with actual implementation
      setApplications(prev => 
        prev.map(app => 
          app.id === selectedApplication.id 
            ? { 
                ...app, 
                status: 'interview_scheduled',
                interview: interviewData
              }
            : app
        )
      );
      
      setAlert({
        show: true,
        message: 'Interview scheduled successfully',
        severity: 'success'
      });
      setInterviewDialogOpen(false);
      setInterviewData({
        date: '',
        time: '',
        type: 'video',
        location: '',
        meetingLink: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error scheduling interview:', error);
      setAlert({
        show: true,
        message: 'Failed to schedule interview',
        severity: 'error'
      });
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.applicant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.applicant.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    const matchesJob = jobFilter === 'all' || app.jobId.toString() === jobFilter;
    
    return matchesSearch && matchesStatus && matchesJob;
  });

  const getApplicationsByStatus = (status) => {
    return applications.filter(app => app.status === status);
  };

  const tabData = [
    { label: 'All Applications', count: applications.length },
    { label: 'New', count: getApplicationsByStatus('submitted').length },
    { label: 'Under Review', count: getApplicationsByStatus('under_review').length },
    { label: 'Shortlisted', count: getApplicationsByStatus('shortlisted').length },
    { label: 'Interviews', count: getApplicationsByStatus('interview_scheduled').length + getApplicationsByStatus('interviewed').length }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Job Applications Management
      </Typography>

      {alert.show && (
        <Alert 
          severity={alert.severity} 
          onClose={() => setAlert({ ...alert, show: false })}
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Applications
              </Typography>
              <Typography variant="h4">
                {applications.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                New Applications
              </Typography>
              <Typography variant="h4" color="primary">
                {getApplicationsByStatus('submitted').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Interviews Scheduled
              </Typography>
              <Typography variant="h4" color="warning.main">
                {getApplicationsByStatus('interview_scheduled').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Offers Extended
              </Typography>
              <Typography variant="h4" color="success.main">
                {getApplicationsByStatus('offer_extended').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {Object.entries(statusLabels).map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Job Position</InputLabel>
                <Select
                  value={jobFilter}
                  label="Job Position"
                  onChange={(e) => setJobFilter(e.target.value)}
                >
                  <MenuItem value="all">All Jobs</MenuItem>
                  {jobs.map((job) => (
                    <MenuItem key={job.id} value={job.id.toString()}>
                      {job.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Applications Table */}
      <Card>
        <CardContent>
          <Tabs 
            value={selectedTab} 
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            {tabData.map((tab, index) => (
              <Tab 
                key={index}
                label={
                  <Badge badgeContent={tab.count} color="primary">
                    {tab.label}
                  </Badge>
                }
              />
            ))}
          </Tabs>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Applicant</TableCell>
                  <TableCell>Job Position</TableCell>
                  <TableCell>Applied Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Experience</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredApplications.map((application) => (
                  <TableRow key={application.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2 }}>
                          {application.applicant.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {application.applicant.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {application.applicant.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{application.jobTitle}</TableCell>
                    <TableCell>
                      {new Date(application.appliedDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[application.status]}
                        color={statusColors[application.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{application.experience}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          onClick={() => {
                            setSelectedApplication(application);
                            setDetailsDialogOpen(true);
                          }}
                          size="small"
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send Email">
                        <IconButton
                          onClick={() => window.open(`mailto:${application.applicant.email}`)}
                          size="small"
                        >
                          <EmailIcon />
                        </IconButton>
                      </Tooltip>
                      {application.status === 'submitted' && (
                        <Tooltip title="Shortlist">
                          <IconButton
                            onClick={() => handleStatusUpdate(application.id, 'shortlisted')}
                            size="small"
                            color="primary"
                          >
                            <ApproveIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {application.status === 'shortlisted' && (
                        <Tooltip title="Schedule Interview">
                          <IconButton
                            onClick={() => {
                              setSelectedApplication(application);
                              setInterviewDialogOpen(true);
                            }}
                            size="small"
                            color="warning"
                          >
                            <ScheduleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Application Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Application Details
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Applicant Information
                  </Typography>
                  <Typography><strong>Name:</strong> {selectedApplication.applicant.name}</Typography>
                  <Typography><strong>Email:</strong> {selectedApplication.applicant.email}</Typography>
                  <Typography><strong>Phone:</strong> {selectedApplication.applicant.phone}</Typography>
                  <Typography><strong>Experience:</strong> {selectedApplication.experience}</Typography>
                  <Typography><strong>Expected Salary:</strong> {selectedApplication.expectedSalary}</Typography>
                  <Typography><strong>Available From:</strong> {selectedApplication.availableFrom}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Application Details
                  </Typography>
                  <Typography><strong>Position:</strong> {selectedApplication.jobTitle}</Typography>
                  <Typography><strong>Applied Date:</strong> {new Date(selectedApplication.appliedDate).toLocaleDateString()}</Typography>
          <Typography><strong>Status:</strong> 
            <Chip
              label={statusLabels[selectedApplication.status]}
              color={statusColors[selectedApplication.status]}
              size="small"
              sx={{ ml: 1 }}
            />
          </Typography>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>New Status</InputLabel>
              <Select
                value={newStatus}
                label="New Status"
                onChange={(e) => {
                  setNewStatus(e.target.value);
                  setStatusError('');
                }}
              >
                {Object.entries(statusLabels)
                  .filter(([key]) => key !== (selectedApplication?.status || ''))
                  .map(([key, label]) => (
                    <MenuItem key={key} value={key}>{label}</MenuItem>
                  ))}
              </Select>
            </FormControl>
            {statusError && (
              <Alert severity="error" sx={{ mt: 2 }}>{statusError}</Alert>
            )}
          </Box>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Cover Letter
              </Typography>
              <Typography variant="body2" paragraph>
                {selectedApplication.coverLetter}
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Button
                  startIcon={<DownloadIcon />}
                  variant="outlined"
                  onClick={() => {
                    // Mock download - replace with actual implementation
                    alert('Resume download would start here');
                  }}
                >
                  Download Resume
                </Button>
                {selectedApplication.applicant.linkedIn && (
                  <Button
                    startIcon={<LinkedInIcon />}
                    variant="outlined"
                    sx={{ ml: 1 }}
                    onClick={() => window.open(selectedApplication.applicant.linkedIn, '_blank')}
                  >
                    LinkedIn Profile
                  </Button>
                )}
              </Box>

              {selectedApplication.interview && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Interview Details
                  </Typography>
                  <Typography><strong>Date:</strong> {selectedApplication.interview.date}</Typography>
                  <Typography><strong>Time:</strong> {selectedApplication.interview.time}</Typography>
                  <Typography><strong>Type:</strong> {selectedApplication.interview.type}</Typography>
                  {selectedApplication.interview.meetingLink && (
                    <Typography>
                      <strong>Meeting Link:</strong> 
                      <Button 
                        size="small" 
                        onClick={() => window.open(selectedApplication.interview.meetingLink, '_blank')}
                      >
                        Join Meeting
                      </Button>
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            Close
          </Button>
          <Button
            onClick={() => {
              if (!newStatus) {
                setStatusError('Please select a new status');
                return;
              }
              if (newStatus === selectedApplication?.status) {
                setStatusError('Selected status is the same as current');
                return;
              }
              handleStatusUpdate(selectedApplication?.id, newStatus);
            }}
            variant="contained"
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog 
        open={statusUpdateDialogOpen} 
        onClose={() => setStatusUpdateDialogOpen(false)}
      >
        <DialogTitle>Update Application Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>New Status</InputLabel>
            <Select
              value={newStatus}
              label="New Status"
              onChange={(e) => {
                setNewStatus(e.target.value);
                setStatusError('');
              }}
            >
              {Object.entries(statusLabels)
                .filter(([key]) => key !== (selectedApplication?.status || ''))
                .map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
            </Select>
          </FormControl>
          {statusError && (
            <Alert severity="error" sx={{ mt: 2 }}>{statusError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusUpdateDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!newStatus) {
                setStatusError('Please select a new status');
                return;
              }
              if (newStatus === selectedApplication?.status) {
                setStatusError('Selected status is the same as current');
                return;
              }
              handleStatusUpdate(selectedApplication?.id, newStatus);
            }}
            variant="contained"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Interview Scheduling Dialog */}
      <Dialog 
        open={interviewDialogOpen} 
        onClose={() => setInterviewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schedule Interview</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={interviewData.date}
                onChange={(e) => setInterviewData({ ...interviewData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={interviewData.time}
                onChange={(e) => setInterviewData({ ...interviewData, time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Interview Type</InputLabel>
                <Select
                  value={interviewData.type}
                  label="Interview Type"
                  onChange={(e) => setInterviewData({ ...interviewData, type: e.target.value })}
                >
                  <MenuItem value="video">Video Call</MenuItem>
                  <MenuItem value="phone">Phone Call</MenuItem>
                  <MenuItem value="in-person">In Person</MenuItem>
                  <MenuItem value="online">Online Assessment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {interviewData.type === 'video' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Meeting Link"
                  value={interviewData.meetingLink}
                  onChange={(e) => setInterviewData({ ...interviewData, meetingLink: e.target.value })}
                  placeholder="https://meet.google.com/..."
                />
              </Grid>
            )}
            {interviewData.type === 'in-person' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location"
                  value={interviewData.location}
                  onChange={(e) => setInterviewData({ ...interviewData, location: e.target.value })}
                  placeholder="Office address or meeting location"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={interviewData.notes}
                onChange={(e) => setInterviewData({ ...interviewData, notes: e.target.value })}
                placeholder="Additional notes for the candidate..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterviewDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleScheduleInterview}
            variant="contained"
            disabled={!interviewData.date || !interviewData.time}
          >
            Schedule Interview
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default JobApplications;